import React, { useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    useCreateTestScript,
    useDeleteTestScript,
    useReplaceTestScriptFile,
    useTestScripts,
    useUpdateTestScript,
} from '../../hooks/testScripts';
import type { TestScript, TestScriptPurpose } from '../../types/types';
import { t } from '../../i18n/t';
import testScriptsService from '../../api/testScriptsService';

const PURPOSES: TestScriptPurpose[] = ['pdf', 'indicators'];
const MAX_SCRIPT_SIZE = 2 * 1024 * 1024;

const formatDate = (s: string) =>
    new Date(s).toLocaleString(navigator.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const purposeLabel = (purpose: TestScriptPurpose) =>
    purpose === 'pdf' ? t('testScripts.sectionPdf') : t('testScripts.sectionIndicators');

const DashboardTestScripts = () => {
    const [purpose, setPurpose] = useState<TestScriptPurpose>('pdf');
    const { data: scripts = [], isLoading, refetch } = useTestScripts(purpose);
    const deleteMutation = useDeleteTestScript();
    const [uploadOpen, setUploadOpen] = useState(false);
    const [editingScript, setEditingScript] = useState<TestScript | null>(null);
    const [replacingScript, setReplacingScript] = useState<TestScript | null>(null);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const handleDelete = async (script: TestScript) => {
        if (!window.confirm(t('testScripts.deleteConfirm', { name: script.name }))) return;
        try {
            await deleteMutation.mutateAsync(script);
        } catch (e) {
            console.error(e);
            alert(t('testScripts.deleteFailed'));
        }
    };

    const handleDownload = async (script: TestScript) => {
        setDownloadingId(script.id);
        try {
            const blob = await testScriptsService.download(script.id);
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = script.originalFilename?.split(/[\\/]/).pop() || 'script.py';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
            console.error(e);
            alert(t('testScripts.downloadFailed'));
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('testScripts.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setUploadOpen(true)}>
                    {t('testScripts.upload')}
                </Button>
                <Tooltip title={t('testScripts.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={purpose}
                    onChange={(_, value: TestScriptPurpose) => setPurpose(value)}
                    variant="scrollable"
                >
                    {PURPOSES.map((p) => (
                        <Tab key={p} value={p} label={purposeLabel(p)} />
                    ))}
                </Tabs>
            </Paper>

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>{t('testScripts.colName')}</TableCell>
                                <TableCell>{t('testScripts.colPurpose')}</TableCell>
                                <TableCell>{t('testScripts.colFilename')}</TableCell>
                                <TableCell>{t('testScripts.colCreated')}</TableCell>
                                <TableCell align="right">{t('testScripts.colActions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scripts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        {t('testScripts.empty')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                scripts.map((script) => (
                                    <TableRow key={script.id}>
                                        <TableCell>{script.id}</TableCell>
                                        <TableCell>{script.name}</TableCell>
                                        <TableCell>{purposeLabel(script.purpose)}</TableCell>
                                        <TableCell>{script.originalFilename ?? '-'}</TableCell>
                                        <TableCell>{formatDate(script.created_at)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={t('testScripts.download')}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDownload(script)}
                                                        disabled={downloadingId === script.id}
                                                    >
                                                        {downloadingId === script.id ? (
                                                            <CircularProgress size={18} />
                                                        ) : (
                                                            <DownloadIcon />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={t('testScripts.replaceFile')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setReplacingScript(script)}
                                                >
                                                    <SwapHorizIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('common.edit')}>
                                                <IconButton size="small" onClick={() => setEditingScript(script)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('common.delete')}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(script)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {uploadOpen && <UploadScriptDialog purpose={purpose} onClose={() => setUploadOpen(false)} />}
            {editingScript && (
                <EditScriptDialog script={editingScript} onClose={() => setEditingScript(null)} />
            )}
            {replacingScript && (
                <ReplaceScriptDialog
                    script={replacingScript}
                    onClose={() => setReplacingScript(null)}
                />
            )}
        </Box>
    );
};

const UploadScriptDialog: React.FC<{ purpose: TestScriptPurpose; onClose: () => void }> = ({
    purpose,
    onClose,
}) => {
    const createMutation = useCreateTestScript();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [name, setName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!f) return;
        if (!f.name.toLowerCase().endsWith('.py')) {
            alert(t('testScripts.invalidFormat'));
            return;
        }
        setFile(f);
        if (!name.trim()) {
            setName(f.name.replace(/\.[^.]+$/, ''));
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            alert(t('testScripts.enterName'));
            return;
        }
        if (!file) {
            alert(t('testScripts.pickFile'));
            return;
        }
        setSaving(true);
        try {
            await createMutation.mutateAsync({ name: name.trim(), purpose, file });
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('testScripts.uploadFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{t('testScripts.uploadDialog')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('testScripts.fieldName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('testScripts.fieldPurpose')}
                        value={purposeLabel(purpose)}
                        size="small"
                        fullWidth
                        disabled
                    />
                    <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {file ? file.name : t('testScripts.pickFile')}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".py"
                        hidden
                        onChange={handleFileChange}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ReplaceScriptDialog: React.FC<{ script: TestScript; onClose: () => void }> = ({
    script,
    onClose,
}) => {
    const replaceMutation = useReplaceTestScriptFile();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!selectedFile) return;
        if (
            !selectedFile.name.toLowerCase().endsWith('.py') ||
            selectedFile.size > MAX_SCRIPT_SIZE
        ) {
            alert(t('testScripts.invalidFormat'));
            return;
        }
        setFile(selectedFile);
    };

    const handleReplace = async () => {
        if (!file) {
            alert(t('testScripts.pickFile'));
            return;
        }
        setSaving(true);
        try {
            await replaceMutation.mutateAsync({ script, file });
            alert(t('testScripts.replaceSuccess'));
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('testScripts.replaceFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{t('testScripts.replaceDialog')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('testScripts.fieldName')}
                        value={script.name}
                        size="small"
                        fullWidth
                        disabled
                    />
                    <TextField
                        label={t('testScripts.currentFile')}
                        value={script.originalFilename || 'script.py'}
                        size="small"
                        fullWidth
                        disabled
                    />
                    <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {file ? file.name : t('testScripts.chooseReplacement')}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".py"
                        hidden
                        onChange={handleFileChange}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button
                    variant="contained"
                    onClick={handleReplace}
                    disabled={saving || !file}
                >
                    {saving ? <CircularProgress size={20} /> : t('testScripts.replaceFile')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const EditScriptDialog: React.FC<{ script: TestScript; onClose: () => void }> = ({ script, onClose }) => {
    const updateMutation = useUpdateTestScript();
    const [name, setName] = useState(script.name);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!name.trim()) {
            alert(t('testScripts.enterName'));
            return;
        }
        setSaving(true);
        try {
            await updateMutation.mutateAsync({ id: script.id, dto: { name: name.trim() } });
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('testScripts.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{t('testScripts.editDialog')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('testScripts.fieldName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardTestScripts;
